import { memo } from 'react';

import { useQuery } from '@apollo/client';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';

import type { GetTraceSnapshotByHashQuery } from '@/apollo/graphql';
import { DiffViewer } from '@/components/monaco/diffViewer/diffViewer';
import { GET_TRACE_SNAPSHOT_BY_HASH } from '@/gql/queries/traceSnapshot';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentLinkHash: string;
  prevLinkHash: string | null | undefined;
};

export const DataDiffModal = memo(function DataDiffModal({
  open,
  onOpenChange,
  currentLinkHash,
  prevLinkHash
}: Props) {
  // Query current snapshot
  const {
    data: currentData,
    loading: loadingCurrent,
    error: errorCurrent
  } = useQuery<GetTraceSnapshotByHashQuery>(GET_TRACE_SNAPSHOT_BY_HASH, {
    variables: { hash: currentLinkHash },
    skip: !open
  });

  // Query previous snapshot (if exists)
  const {
    data: prevData,
    loading: loadingPrev,
    error: errorPrev
  } = useQuery<GetTraceSnapshotByHashQuery>(GET_TRACE_SNAPSHOT_BY_HASH, {
    variables: { hash: prevLinkHash },
    skip: !(open && prevLinkHash)
  });

  const isLoading = loadingCurrent || loadingPrev;
  const hasError = errorCurrent || errorPrev;

  // Extract snapshot data
  const currentSnapshot = {
    ...currentData?.traceSnapshotByHash,
    meta: undefined
  };
  const prevSnapshot = {
    ...prevData?.traceSnapshotByHash,
    meta: undefined
  };

  // Show empty state if no snapshots exist
  const noSnapshots = !(
    isLoading ||
    currentSnapshot ||
    prevSnapshot ||
    hasError
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex h-[90vh] max-w-[95vw] flex-col gap-0 p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Data changes diff</DialogTitle>
          <DialogDescription>
            Comparing data changes between current and previous actions
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground text-sm">
                  Loading snapshots...
                </p>
              </div>
            </div>
          )}

          {hasError && (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <div>
                  <h3 className="font-semibold text-foreground">
                    Error loading snapshots
                  </h3>
                  <p className="mt-2 text-muted-foreground text-sm">
                    {errorCurrent?.message || errorPrev?.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {noSnapshots && (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border bg-muted/50 p-6 text-center">
                <AlertCircle className="h-8 w-8 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-foreground">
                    No snapshot data available
                  </h3>
                  <p className="mt-2 text-muted-foreground text-sm">
                    Snapshot data is not available for these actions. This may
                    be because the feature is not enabled or snapshots have not
                    been created yet.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!(isLoading || hasError) && (currentSnapshot || prevSnapshot) && (
            <div className="flex-1 overflow-hidden">
              <DiffViewer
                after={currentSnapshot || {}}
                before={prevSnapshot || {}}
              />
            </div>
          )}
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
